import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
  ProjectWithJobType,
} from "@repo/app-types/entities/project";
import { ProjectDetailsTabPath } from "@repo/app-types/helpers/adminAppRoutes";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { type FC, useEffect, useState } from "react";
import { Outlet } from "react-router";
import { getAxiosInstanceWithAuth } from "../api/axiosConfig.js";
import { Tab, Tabs } from "../components/projectDetailsTabs.js";
import { fetchAssessmentById } from "../features/assessments/assessmentsSlice.js";
import { selectCompany } from "../features/company/companySlice.js";
import { ImportContactsSlideover } from "../features/contacts/importSlideover.js";
import {
  NotificationType,
  showNotification,
} from "../features/notificationsOverlay/notificationsSlice.js";
import { DeleteProjectModal } from "../features/projects/deleteProjectModal.js";
import { ProjectMoreMenu } from "../features/projects/projectMoreMenu.js";
import { ProjectNameEditor } from "../features/projects/projectNameEditor.js";
import { ProjectOwnersWidget } from "../features/projects/projectOwnersWidget.js";
import { LearningJourneyBuilderPage } from "../features/projects/projectSettings/training/learningJourneyBuilderPage.js";
import { TrainingProjectGeneralSettings } from "../features/projects/projectSettings/training/trainingProjectGeneralSettings.js";
import { updateProject } from "../features/projects/projectsSlice.js";
import { fetchTeammates } from "../features/teammates/teammatesSlice.js";
import { TrainingInsightsTab } from "../features/trainingInsights/trainingInsightsTab.js";
import { TrainingParticipantsTab } from "../features/trainingParticipants/trainingParticipantsTab.js";
import { useAppDispatch, useAppSelector } from "../hooks/hook.js";
import type { ProjectDetailsTabsContext } from "./projectDetailsTabRoutes.js";

interface TrainingProjectDetailsPageProps {
  project: ProjectWithInterviewCount & ProjectWithJobType;
}

export const TrainingProjectDetailsPage: FC<
  TrainingProjectDetailsPageProps
> = ({ project }) => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);

  if (!company) {
    throw new Error("Company not found");
  }

  useEffect(() => {
    dispatch(fetchTeammates());
  }, [dispatch]);

  useEffect(() => {
    if (project.job_type.assessment_id) {
      dispatch(fetchAssessmentById(project.job_type.assessment_id));
    }
  }, [dispatch, project.job_type.assessment_id]);

  const [isDeleteWarningModalOpen, setIsDeleteWarningModalOpen] =
    useState<boolean>(false);
  const [isCsvImportSlideoverOpen, setIsCsvImportSlideoverOpen] =
    useState<boolean>(false);
  const [isCsvExportModalOpen, setIsCsvExportModalOpen] = useState(false);
  const [isCsvExportLoading, setIsCsvExportLoading] = useState(false);

  const onSaveProject = async (
    changes: ProjectUpdate,
    hidesNotifications = false,
  ) => {
    try {
      await dispatch(
        updateProject({
          projectId: project.id,
          changes,
        }),
      );

      if (!hidesNotifications) {
        showNotification(dispatch, {
          id: `project-${project.id}-updated-${new Date().getTime()}`,
          primaryMessage: `Training successfully updated`,
          type: NotificationType.SUCCESS,
        });
      }
    } catch (err) {
      console.error("error updating project", err);
      showNotification(dispatch, {
        id: `project-${project.id}-update-failed-${new Date().getTime()}`,
        primaryMessage: `Failed to update training`,
        type: NotificationType.FAILURE,
      });
    }
  };

  // TODO: De-dupe with projectDetailsPage.tsx.
  const handleCsvExport = async () => {
    setIsCsvExportLoading(true);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.get(`/projects/${project.id}/csv-export`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${project.name}_trainings.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showNotification(dispatch, {
        id: `project-${project.id}-csv-export-success-${new Date().getTime()}`,
        primaryMessage: "CSV export successful",
        type: NotificationType.SUCCESS,
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      showNotification(dispatch, {
        id: `project-${project.id}-csv-export-error-${new Date().getTime()}`,
        primaryMessage: "Failed to export CSV",
        type: NotificationType.FAILURE,
      });
    } finally {
      setIsCsvExportLoading(false);
      setIsCsvExportModalOpen(false);
    }
  };

  const renderTabs = () => (
    <Tabs>
      <Tab to={ProjectDetailsTabPath.Overview}>Overview</Tab>
      <Tab to={ProjectDetailsTabPath.ProjectConfiguration}>Configuration</Tab>
      <Tab to={ProjectDetailsTabPath.Participants}>Participants</Tab>
      <Tab to={ProjectDetailsTabPath.Insights}>Insights</Tab>
    </Tabs>
  );

  // The tab routes live in the router (see the `/project/:projectId` subtree);
  // this variant supplies its tabs' content through the outlet context.
  const tabsContext: ProjectDetailsTabsContext = {
    overviewTab: (
      <TrainingProjectGeneralSettings
        project={project}
        company={company}
        onSaveProject={onSaveProject}
      />
    ),
    configurationTab: (
      <div className="h-full w-full">
        <div className="-mx-4 h-full overflow-hidden">
          <LearningJourneyBuilderPage project={project} />
        </div>
      </div>
    ),
    participantsTab: <TrainingParticipantsTab project={project} />,
    insightsTab: <TrainingInsightsTab project={project} />,
    defaultTabPath: ProjectDetailsTabPath.Overview,
  };

  return (
    <>
      <div className="grid h-full min-h-full w-full grid-rows-[auto_1fr] justify-items-center">
        <div className="w-full px-4">
          <div className="mx-auto w-full max-w-4xl border-b border-border-light">
            <div className="flex min-h-[52px] items-center justify-between">
              <div className="flex flex-row items-center space-x-2">
                <ProjectNameEditor
                  name={project.name}
                  onChange={(newName) => onSaveProject({ name: newName }, true)}
                />
                {renderTabs()}
              </div>
              <div className="flex items-center space-x-3">
                <ProjectOwnersWidget
                  teammateIds={project.owner_teammate_ids}
                  onTeammateIdsSave={(teammateIds) =>
                    onSaveProject({ owner_teammate_ids: teammateIds }, true)
                  }
                />
                <ProjectMoreMenu
                  project={project}
                  onClickDeleteProject={() => {
                    setIsDeleteWarningModalOpen(true);
                  }}
                  onClickCsvImport={() => {
                    setIsCsvImportSlideoverOpen(true);
                  }}
                  onClickCsvExport={() => {
                    setIsCsvExportModalOpen(true);
                  }}
                  onClickRegenerateInterviewLink={() => {}}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="relative h-full w-full overflow-auto px-4">
          <Outlet context={tabsContext} />
        </div>
      </div>

      <DeleteProjectModal
        isOpen={isDeleteWarningModalOpen}
        onClose={() => setIsDeleteWarningModalOpen(false)}
        projectId={project.id}
        interviewAgentType={project.settings?.interview_agent_type}
      />

      <ImportContactsSlideover
        isOpen={isCsvImportSlideoverOpen}
        defaultProjectId={project.id}
        projectTypeFilter="training"
        onClose={() => setIsCsvImportSlideoverOpen(false)}
      />

      <SimpleModal
        isOpen={isCsvExportModalOpen}
        variant={SimpleModalVariant.Standard}
        onCancel={() => {
          setIsCsvExportModalOpen(false);
        }}
        title="Export trainings to CSV"
        subtitle="Training data, transcripts, and insights for this project will be exported to a CSV. This may take up to a minute."
        confirmButtonText="Continue"
        isPrimaryButtonLoading={isCsvExportLoading}
        onConfirm={handleCsvExport}
      />
    </>
  );
};
